/**
 * Household Burden Calculator for SAFECARE Act
 * Compares current household health costs vs SAFECARE contributions
 */

import { calculateWagePassThrough } from './wagePassThrough.js';
import { calculateQualityScore } from './qualityModel.js';
import { SAFECARE_BILL_PARAMETERS } from './billParameters.js';
import { calculateIndividualContribution } from './revenueCalculator.js';

/**
 * Calculate household burden for a specific income decile
 */
export function calculateDecileBurden(decile, config) {
    const income = decile.avgIncome;

    // Current system burden
    // 1. Premiums + OOP (from data)
    const currentPremiumOOP = decile.totalBurden;

    // 2. Medicare HI Tax (Repealed by Section 205)
    // 1.45% of all earned income + 0.9% Additional Medicare Tax > $200k
    // Note: We use total income as proxy for earned income here (simplified)
    const currentHITax = income * 0.0145;
    const currentAddMedicareTax = Math.max(0, income - 200000) * 0.009;
    const totalRepealedTaxes = currentHITax + currentAddMedicareTax;

    // Total Current Burden = Premium + OOP + Repealed Taxes
    const totalCurrentBurden = currentPremiumOOP + totalRepealedTaxes;
    const currentPercent = (totalCurrentBurden / income) * 100;

    // SAFECARE burden (individual contribution + copayments)
    const safecareContribution = calculateIndividualContribution(income, config);

    // Estimate copayments (most people will hit the cap)
    const estimatedCopayments = Math.min(config.annualCopaymentCap,
        decile.avgOOP * 0.3); // Assume copays are ~30% of current OOP

    const safecareCashCost = safecareContribution + estimatedCopayments;

    // === REFINE: WAGE INCIDENCE (Sector-Based Pass-Through) ===
    // Sec 202 requires employers to pass through savings, but effectiveness varies by sector.

    // Default sectors for aggregate simulation if not present in decile data
    // Decile 1-4: Private/Service (Low bargaining)
    // Decile 5-8: Mix of Union/Public/Private
    // Decile 9-10: High Skill Private

    // We mock the household object structure expected by the new model
    // In a full microsim, these would be real household attributes.
    const mockHousehold = {
        is_self_employed: false, // Default to employee for this calculation
        union_member: decile.decile >= 5 && decile.decile <= 8, // Proxy: Middle class more likely union
        public_sector_employee: decile.decile === 7 || decile.decile === 8, // Proxy
    };

    // New Employer Tax (8%)
    // Estimate taxable wages as portion of income (conservative 70-90% for most)
    const wageShare = decile.decile >= 9 ? 0.7 : 0.9;
    const estimatedWages = income * wageShare;
    const newEmployerTax = estimatedWages * 0.08;

    // Old Employer Premium (from data)
    const oldEmployerPremium = decile.avgEmployerPremium || 0;

    // Calculate Pass-Through
    // Net Savings to Employer = Old Premium - New Tax
    const netEmployerSavings = Math.max(0, oldEmployerPremium - newEmployerTax);

    // Use new WagePassThrough model
    // Assuming Year 2030 (Steady State) for this snapshot view, or pass in config year
    const simulationYear = 2030;
    const passThroughResult = calculateWagePassThrough(mockHousehold, netEmployerSavings, simulationYear);

    const wageDividend = passThroughResult.wageIncrease;

    // Note: If New Tax > Old Premium, employer pays more.
    // In strict economic theory, this might REDUCE wages.
    // The bill (Sec 202) forbids reducing wages to pay for the tax.
    // So we floor the wage change at 0 for the "dividend", but the employer still bears the cost.

    // Total economic impact:
    // Household Cash: (Old Employee Premium + Old OOP) - (New Tax + New Copay) + Wage Dividend

    // Total Economic Burden under SAFECARE
    // = Cash Cost - Wage Increase (or + Wage Decrease)
    // We present "Savings" as (Current Total - Reform Total)
    // Current Total (Economic) = Prem + OOP + Taxes + (Employer Prem passed through as lower wage)
    // Reform Total (Economic) = Contribution + Copay + (Employer Tax passed through)

    // Simplified: Cash Savings + Wage Dividend
    const cashSavings = totalCurrentBurden - safecareCashCost;
    const totalSavings = cashSavings + wageDividend;

    // === QUALITY SCORE (New Integration) ===
    // Calculate 0-100 quality score based on access, coverage, and provider density
    const qualityScore = calculateQualityScore(mockHousehold, 'safecare', {
        population_density: decile.population_density || 100,
        is_frontier: decile.is_frontier || false
    });

    // Effective incidence-adjusted burden for display percentage
    const incidenceAdjustedBurden = safecareCashCost - wageDividend;

    return {
        decile: decile.decile,
        label: decile.label,
        income,
        current: {
            premium: decile.avgPremium,
            oop: decile.avgOOP,
            taxes: totalRepealedTaxes,
            employerPremium: oldEmployerPremium, // Display for transparency
            total: totalCurrentBurden,
            percent: currentPercent
        },
        safecare: {
            // Detailed breakdowns
            contribution: safecareContribution,
            copayments: estimatedCopayments,
            employerTax: newEmployerTax, // For transparency
            totalCash: safecareCashCost,

            // Wage Incidence
            wageDividend: wageDividend,
            wagePassThroughRate: passThroughResult.rate,
            wagePassThroughSector: passThroughResult.sector,

            // Quality
            qualityScore: qualityScore,

            // Net Impact (Economic)
            totalEconomic: incidenceAdjustedBurden,
            total: incidenceAdjustedBurden, // REQUIRED: used by aggregate calculator
            percent: (incidenceAdjustedBurden / income) * 100 // Economic burden %
        },
        savings: totalSavings, // Use Total Economic Savings for the main "Better Off" metric
        cashSavings,
        savingsPercent: (totalSavings / (totalCurrentBurden + oldEmployerPremium)) * 100, // Denominator includes full compensation
        isBetterOff: totalSavings > 0
    };
}

/**
 * Calculate household burden for all income deciles
 */
export function calculateHouseholdBurdenByDecile(baselineData, config) {
    return baselineData.householdBurden.deciles.map(decile =>
        calculateDecileBurden(decile, config)
    );
}

/**
 * Calculate aggregate household savings across all deciles
 */
export function calculateAggregateHouseholdSavings(burdenByDecile) {
    const totalPopulation = 335000000;
    const peoplePerDecile = totalPopulation / 10;

    let totalCurrentBurden = 0;
    let totalSafecareBurden = 0;

    burdenByDecile.forEach(decile => {
        totalCurrentBurden += decile.current.total * peoplePerDecile;
        totalSafecareBurden += decile.safecare.total * peoplePerDecile;
    });

    const totalSavings = totalCurrentBurden - totalSafecareBurden;

    return {
        totalCurrentBurden,
        totalSafecareBurden,
        totalSavings,
        savingsPercent: (totalSavings / totalCurrentBurden) * 100,
        avgSavingsPerPerson: totalSavings / totalPopulation
    };
}
